import { Fragment, PureComponent, ReactNode } from 'react';
import { PatientDetailsVisits } from '../../../../store/reducers/prescriptionPad';
import { Table } from 'antd';
import { Tool, VitalsEntity } from '../../../../store/types/prescription';
import moment from 'moment';
import { VitalsEmptyCard } from './VitalsEmptyCard';
import { vitalsIcon } from '../../../common/Icons';
import { CALCULATORS } from '../../../../constants';
import { CALCULATOR_VIEW } from './calculatorsTypes';

type Props = {
    visits?: PatientDetailsVisits;
    onRefresh?: () => void;
};

type Visit = Tool & { date: string; visitId: string };
type VitalUnits = { id: string; name: string };
type AllVital = { allVital: VitalUnits[] };
type TableRow = {
    [key: string]: Visit | { allVital?: VitalUnits[] };
};
const vitalIcon = vitalsIcon('fill-white');
const defaultScrollValues = { x: '100%', y: '100%' };
class Vitals extends PureComponent<Props> {
    filterVisitesWithVitalsOnly = () => {
        const filteredVitals =
            this.props.visits?.data
                ?.map((visit) => visit.vitals && visit.vitals.length > 0 && visit)
                .filter(Boolean) || [];
        return filteredVitals as Visit[];
    };

    extractAllUnits = (vitalsArray: Visit[]) => {
        const allVitalsUnit: VitalUnits[] = [];

        vitalsArray?.forEach((visit) => {
            visit?.vitals?.forEach((vital) => {
                const calcVital = CALCULATORS.filter(
                    (calc) => calc.view === CALCULATOR_VIEW.VITALS,
                ).find((calc) => calc.result.id === vital.id);

                allVitalsUnit.push({
                    id: vital.id,
                    name: calcVital
                        ? calcVital.result.name
                        : `${vital.name} ${vital.value.unit ? `(${vital.value.unit})` : ''}`,
                });
            });
        });

        const uniqueVitalsUnit = allVitalsUnit.filter(
            (a, i) => allVitalsUnit.findIndex((s) => a.id === s.id) === i,
        );

        return uniqueVitalsUnit;
    };

    setTitle = (i: number, visits: Visit[]) => {
        return (
            <p>
                {moment(visits?.[i]?.date).isSame(moment(), 'day') ? (
                    <div className="mx-4  flex justify-center py-4">
                        <p className="w-min whitespace-nowrap py-2 px-8 border border-darwin-accent-symptoms-blue-800 rounded-4 font-700 text-11 uppercase text-darwin-accent-symptoms-blue-800">
                            today
                        </p>
                    </div>
                ) : (
                    moment(visits?.[i]?.date).format("DD MMM'YY")
                )}
            </p>
        );
    };

    renderName = (vitalUnit: AllVital) => {
        return (
            <Fragment>
                <div className="flex flex-col space-y-9">
                    {vitalUnit?.allVital?.map((vitalData) => {
                        return (
                            <div className={`flex justify-between items-center space-x-5 h-30`}>
                                <p className="text-darwin-neutral-900">{vitalData.name}</p>
                            </div>
                        );
                    })}
                </div>
            </Fragment>
        );
    };

    searchMatchingVital = (vitalData: VitalUnits, vitals: VitalsEntity[]) => {
        return vitals.find((vital) => vital.id === vitalData.id);
    };

    renderVitals = (visit: Visit, row: TableRow) => {
        return (
            <Fragment>
                <div className="flex items-center flex-col space-y-9">
                    {(row['col0'] as AllVital)?.allVital?.map((vitalData) => {
                        const matchedVital = this.searchMatchingVital(
                            vitalData,
                            visit.vitals || [],
                        );

                        return (
                            <div className={`flex justify-between items-end space-x-5 min-h-30`}>
                                <p className="font-700">{matchedVital?.value?.qt || '-'}</p>
                            </div>
                        );
                    })}
                </div>
            </Fragment>
        );
    };

    buildColumns = (totalColumns: number, visits: Visit[]) => {
        const columnsArray = [];

        for (let i = 0; i < totalColumns; i++) {
            columnsArray.push({
                title: this.setTitle(i, visits),
                dataIndex: `col${i + 1}`,
                render: this.renderVitals,
                width: 80,
            });
        }
        return columnsArray;
    };

    vitalNameColumn = {
        title: (
            <div className="flex py-8 items-center space-x-8">
                <p className="flex justify-center items-center rounded-4 w-24 h-24 bg-darwin-accent-symptoms-blue-800">
                    {vitalIcon}
                </p>
                <p className="font-700">Vitals</p>
            </div>
        ),
        dataIndex: 'col0',
        render: this.renderName,
        width: 225,
        fixed: true,
    };

    render(): ReactNode {
        const { visits, onRefresh } = this.props;
        const filteredVitals = this.filterVisitesWithVitalsOnly();
        const allVitalsUnitArray = this.extractAllUnits(filteredVitals);
        const columns = [
            this.vitalNameColumn,
            ...this.buildColumns(filteredVitals.length, filteredVitals),
        ];
        if (!allVitalsUnitArray.length || !filteredVitals?.length) {
            return <VitalsEmptyCard fetchState={visits?.state} onRefresh={onRefresh} />;
        }

        const tableData = {
            col0: {
                allVital: allVitalsUnitArray,
            },
            ...filteredVitals.reduce((pre, item, i) => ({ ...pre, [`col${i + 1}`]: item }), {}),
        } as TableRow;

        return (
            <Table
                bordered={false}
                className="vitals-table"
                dataSource={[tableData]}
                pagination={false}
                columns={columns}
                scroll={defaultScrollValues}
            ></Table>
        );
    }
}

export default Vitals;
